import { Sparkles } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import type { DynamicColumnInfo } from "@eduviaos/shared";
import { useTerms } from "@/hooks/useTerms";
import { describeDynamicColumn } from "@/pages/studentImportDynamicColumns";

/**
 * Phase H6c (extracted in H6d) — institute-authored extra-columns
 * panel for the student-import upload screen.
 *
 * Surfaces below the canonical column list when the tenant has an
 * active ``student_record`` FormDefinition (the BE returns
 * ``has_active_form=true`` in that case). The required / optional
 * split is the most important affordance — admins eyeball this before
 * downloading the template to know which cells MUST be filled in.
 *
 * Per Iron Rule #6 the only animation here is a 200ms fade-in via
 * Tailwind's motion-safe utilities. No hover transforms, no row-by-
 * row stagger — the panel is reference material, not interactive.
 */
export interface DynamicColumnsPanelProps {
  dynamicColumns: readonly DynamicColumnInfo[];
  formSlug: string | null;
  formVersion: number | null;
}

export function DynamicColumnsPanel({
  dynamicColumns,
  formSlug,
  formVersion,
}: DynamicColumnsPanelProps) {
  const { ct } = useTerms();
  const requiredCols = dynamicColumns.filter((c) => c.required);
  const optionalCols = dynamicColumns.filter((c) => !c.required);
  return (
    <div
      className="border-primary/30 bg-primary/5 motion-safe:animate-in motion-safe:fade-in grid gap-2 rounded-md border p-3 motion-safe:duration-300"
      data-testid="dynamic-columns-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="text-primary size-3.5" aria-hidden="true" />
        <p className="text-foreground text-xs font-medium uppercase tracking-wide">
          Your extra columns
        </p>
        {formSlug && formVersion != null ? (
          <span className="text-muted-foreground text-[11px] tabular-nums">
            Version {formVersion}
          </span>
        ) : null}
      </div>
      <p className="text-muted-foreground text-[11px] leading-relaxed">
        {ct(
          "These come from your student form. They're added after the standard columns in the downloaded template — fill them in alongside the standard fields.",
        )}
      </p>
      {requiredCols.length > 0 ? (
        <DynamicColumnGroup
          label={`Required (${requiredCols.length})`}
          columns={requiredCols}
          required
        />
      ) : null}
      {optionalCols.length > 0 ? (
        <DynamicColumnGroup
          label={`Optional (${optionalCols.length})`}
          columns={optionalCols}
          required={false}
        />
      ) : null}
    </div>
  );
}

interface DynamicColumnGroupProps {
  label: string;
  columns: readonly DynamicColumnInfo[];
  required: boolean;
}

function DynamicColumnGroup({ label, columns, required }: DynamicColumnGroupProps) {
  return (
    <div className="grid gap-1">
      <p className="text-muted-foreground text-[11px] font-medium tracking-wide">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {columns.map((col) => (
          <code
            key={col.key}
            title={describeDynamicColumn(col)}
            className={cn(
              "rounded-sm px-1.5 py-0.5 font-mono text-[11px]",
              required
                ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                : "bg-muted text-foreground/80",
            )}
            data-field-key={col.key}
            data-field-type={col.field_type}
          >
            {col.label}
            {required ? <span aria-hidden="true"> *</span> : null}
          </code>
        ))}
      </div>
    </div>
  );
}
